import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, RefreshCw, GitMerge, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

export default function BrandAliasesCard({ onMerged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [busyBrand, setBusyBrand] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);  // {registered, aliases: [{raw,count}]} — merges ALL

  const fetchAliases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/brand-aliases');
      if (!res.ok) {
        setItems([]);
        return;
      }
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAliases(); }, [fetchAliases]);

  const toggle = (name) => setExpanded((s) => ({ ...s, [name]: !s[name] }));

  const merge = async ({ registered_brand, aliases }) => {
    setBusyBrand(registered_brand);
    try {
      const body = { registered_brand };
      if (aliases && aliases.length) body.aliases = aliases;
      const res = await apiFetch('/api/brand-aliases/merge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || 'Merge failed');
        return;
      }
      const data = await res.json();
      toast.success(`Merged ${data.reviews_updated} reviews into "${registered_brand}"`);
      setMergeTarget(null);
      await fetchAliases();
      if (typeof onMerged === 'function') onMerged();
    } catch (_) {
      toast.error('Merge failed');
    } finally {
      setBusyBrand(null);
    }
  };

  return (
    <Card className="card-hover" data-testid="brand-aliases-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#2eb4ac]" />
            Brand Aliases
          </CardTitle>
          <CardDescription>
            Raw brand spellings currently in your reviews that fold under a registered brand — merge to canonical to make counts exact and drop the runtime fold cost.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAliases} data-testid="brand-aliases-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="text-sm text-muted-foreground py-6 text-center">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Scanning reviews…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="brand-aliases-empty">
            No aliases detected. Every review is stored under a canonical brand name.
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-2" data-testid="brand-aliases-list">
            {items.map((it) => {
              const isOpen = !!expanded[it.registered];
              const busy = busyBrand === it.registered;
              return (
                <div key={it.registered} className="rounded-lg border border-border/50 bg-background/40" data-testid={`brand-aliases-row-${it.registered}`}>
                  <div className="flex items-center gap-3 px-3 py-2">
                    <button
                      onClick={() => toggle(it.registered)}
                      className="flex items-center gap-2 text-sm font-medium hover:text-[#2eb4ac] transition-colors"
                      data-testid={`brand-aliases-toggle-${it.registered}`}
                    >
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      {it.registered}
                    </button>
                    <Badge variant="secondary" className="text-[10px]">
                      {it.aliases.length} alias{it.aliases.length > 1 ? 'es' : ''}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {it.total_alias_reviews.toLocaleString()} reviews
                    </Badge>
                    <div className="ml-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 border-[#2eb4ac]/40 text-[#2eb4ac] hover:bg-[#2eb4ac]/10"
                        onClick={() => setMergeTarget({ registered: it.registered, aliases: null, total: it.total_alias_reviews, count: it.aliases.length })}
                        disabled={busy}
                        data-testid={`brand-aliases-merge-all-${it.registered}`}
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitMerge className="w-3 h-3" />}
                        Merge all
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-border/40 px-3 py-2 space-y-1.5" data-testid={`brand-aliases-detail-${it.registered}`}>
                      {it.aliases.map((a) => (
                        <div key={a.raw} className="flex items-center gap-2 text-xs" data-testid={`brand-alias-row-${a.raw}`}>
                          <code className="px-1.5 py-0.5 rounded bg-muted/40 font-mono text-[11px]">{a.raw}</code>
                          <Badge variant="outline" className="text-[10px]">{a.count.toLocaleString()} reviews</Badge>
                          <div className="ml-auto">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 gap-1 text-[11px]"
                              onClick={() => setMergeTarget({ registered: it.registered, aliases: [a.raw], total: a.count, count: 1 })}
                              disabled={busy}
                              data-testid={`brand-alias-merge-${a.raw}`}
                            >
                              <GitMerge className="w-3 h-3" /> Merge
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!mergeTarget} onOpenChange={(o) => !o && setMergeTarget(null)}>
        <AlertDialogContent data-testid="brand-aliases-merge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Merge to canonical brand?</AlertDialogTitle>
            <AlertDialogDescription>
              This will rename{' '}
              <span className="font-semibold text-[#2eb4ac]">{mergeTarget?.total?.toLocaleString?.() ?? mergeTarget?.total}</span>{' '}
              review rows{mergeTarget?.count > 1 ? ` across ${mergeTarget.count} alias spellings` : ''} to{' '}
              <span className="font-semibold">&quot;{mergeTarget?.registered}&quot;</span>.
              Downstream SKU URLs and product metadata will also be updated. This is not reversible in the UI — you can re-upload with the original name if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="brand-aliases-merge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => merge({ registered_brand: mergeTarget?.registered, aliases: mergeTarget?.aliases })}
              className="bg-[#2eb4ac] hover:bg-[#2eb4ac]/90"
              data-testid="brand-aliases-merge-confirm"
            >
              Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
